import React, { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { submitLead } from "@/lib/api";
import { SERVICE_OPTIONS, VOLUME_OPTIONS } from "@/data/site";

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRe = /^[+]?[\d\s-]{6,20}$/;

/**
 * Shared lead-capture form used on Contact and Pricing/Quote pages.
 * source: "contact" | "quote"
 */
export const LeadForm = ({ source = "contact", plan = "", defaultService = "", testIdPrefix = "contact" }) => {
  const [form, setForm] = useState({
    name: "",
    company_name: "",
    email: "",
    phone: "",
    service_required: defaultService,
    monthly_volume: "",
    message: "",
    company_website: "", // honeypot
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success

  const set = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name || form.name.trim().length < 2) e.name = "Please enter your name.";
    if (!form.email || !emailRe.test(form.email)) e.email = "Enter a valid email address.";
    if (!form.phone || !phoneRe.test(form.phone)) e.phone = "Enter a valid phone number.";
    if (!form.service_required) e.service_required = "Please select a service.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setStatus("loading");
    try {
      const res = await submitLead({ ...form, plan, source });
      if (res.success) {
        setStatus("success");
        toast.success("Request received!", { description: res.message });
      } else {
        throw new Error("Unexpected response");
      }
    } catch (err) {
      setStatus("idle");
      toast.error("Something went wrong", {
        description: "Please try again or reach us on WhatsApp / phone.",
      });
    }
  };

  if (status === "success") {
    return (
      <div className="et-card p-8 text-center" data-testid={`${testIdPrefix}-form-success`}>
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-50">
          <CheckCircle2 className="h-7 w-7 text-green-600" />
        </div>
        <h3 className="font-display text-xl font-semibold text-slate-900 mt-5">Thank you!</h3>
        <p className="mt-2 text-sm text-slate-600" data-testid="form-success-text">
          Your request has been received. Our team will get in touch with you shortly.
        </p>
        <button
          onClick={() => {
            setForm({ name: "", company_name: "", email: "", phone: "", service_required: defaultService, monthly_volume: "", message: "", company_website: "" });
            setStatus("idle");
          }}
          className="mt-6 text-sm font-semibold text-[var(--et-primary)] hover:underline"
        >
          Submit another request
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="et-card p-6 sm:p-8 space-y-5" data-testid={`${testIdPrefix}-form`} noValidate>
      {/* Honeypot: hidden from users */}
      <div className="hidden" aria-hidden="true">
        <label>Company Website<input tabIndex={-1} autoComplete="off" value={form.company_website} onChange={(e) => set("company_website", e.target.value)} /></label>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Name" required error={errors.name}>
          <Input data-testid={`${testIdPrefix}-name-input`} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your full name" aria-invalid={!!errors.name} />
        </Field>
        <Field label="Company Name" error={errors.company_name}>
          <Input data-testid={`${testIdPrefix}-company-input`} value={form.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="Your company" />
        </Field>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Email" required error={errors.email}>
          <Input type="email" data-testid={`${testIdPrefix}-email-input`} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@company.com" aria-invalid={!!errors.email} />
        </Field>
        <Field label="Phone Number" required error={errors.phone}>
          <Input data-testid={`${testIdPrefix}-phone-input`} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91 98xxxxxxxx" aria-invalid={!!errors.phone} />
        </Field>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Service Required" required error={errors.service_required}>
          <Select value={form.service_required} onValueChange={(v) => set("service_required", v)}>
            <SelectTrigger data-testid={`${testIdPrefix}-service-select`} className="h-11">
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent>
              {SERVICE_OPTIONS.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Monthly Communication Volume">
          <Select value={form.monthly_volume} onValueChange={(v) => set("monthly_volume", v)}>
            <SelectTrigger data-testid={`${testIdPrefix}-volume-select`} className="h-11">
              <SelectValue placeholder="Estimated volume" />
            </SelectTrigger>
            <SelectContent>
              {VOLUME_OPTIONS.map((v) => (
                <SelectItem key={v} value={v}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <Field label="Message">
        <Textarea data-testid={`${testIdPrefix}-message-input`} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us about your communication requirement..." rows={4} />
      </Field>

      <p className="text-xs text-slate-400">Protected by spam filtering. By submitting, you agree to be contacted about your enquiry.</p>

      <button
        type="submit"
        data-testid={`${testIdPrefix}-form-submit-button`}
        disabled={status === "loading"}
        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--et-primary)] px-6 h-12 text-sm sm:text-base font-semibold text-white shadow-[0_10px_24px_rgba(37,99,235,0.22)] transition-colors hover:bg-[var(--et-primary-hover)] disabled:opacity-70 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--et-ring)]"
      >
        {status === "loading" ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</>
        ) : (
          <><Send className="h-4 w-4" /> {source === "quote" ? "Request a Quote" : "Talk to an Expert"}</>
        )}
      </button>
    </form>
  );
};

const Field = ({ label, required, error, children }) => (
  <div className="space-y-1.5">
    <Label className="text-sm font-medium text-slate-700">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    {children}
    {error && <p className="text-xs text-red-500" data-testid="form-error-text">{error}</p>}
  </div>
);
